// The smallest Markdown reader that renders a brief honestly.
//
// `briefs.body_markdown` is Scout's prose on top of the facts, and the admin console has to
// show it as somebody reading their morning brief would see it. There is no Markdown
// dependency in this app and this is not the place to add one: the writer produces headings,
// bullets, bold and paragraphs, and nothing else.
//
// It parses to *data* rather than to HTML on purpose. There is no
// `dangerouslySetInnerHTML` anywhere in this app and there should not be — a brief body is a
// model's output rendered on an authenticated page, which is exactly the shape of an injection
// nobody notices. The renderer walks these blocks and JSX escapes every string in them.
//
// Pure, and imported by `node --test`, so it takes no dependencies at all.

export interface MarkdownSpan {
  text: string;
  bold: boolean;
}

export interface HeadingBlock {
  kind: 'heading';
  level: number;
  spans: MarkdownSpan[];
}

export interface BulletBlock {
  kind: 'bullet';
  items: MarkdownSpan[][];
}

export interface ParagraphBlock {
  kind: 'paragraph';
  spans: MarkdownSpan[];
}

export type MarkdownBlock = HeadingBlock | BulletBlock | ParagraphBlock;

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;

// `**bold**` only. Single `*` and `_` pass through as text: account names carry both, and
// treating them as emphasis is how a company called `*Nine` eats the rest of a line.
export function parseSpans(line: string): MarkdownSpan[] {
  const spans: MarkdownSpan[] = [];
  let rest = line;
  while (rest.length > 0) {
    const open = rest.indexOf('**');
    if (open === -1) break;
    const close = rest.indexOf('**', open + 2);
    if (close === -1) break;
    if (open > 0) spans.push({ text: rest.slice(0, open), bold: false });
    spans.push({ text: rest.slice(open + 2, close), bold: true });
    rest = rest.slice(close + 2);
  }
  if (rest.length > 0) spans.push({ text: rest, bold: false });
  return spans;
}

export function parseMarkdown(body: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let bullets: MarkdownSpan[][] = [];

  // Two open collectors, closed by a heading, a blank line, or the end of the body. Flushing
  // is written out at each site rather than as a helper closure, so the order stays readable.
  for (const rawLine of body.split('\n')) {
    const line = rawLine.replace(/\s+$/, '');
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);

    if (heading) {
      if (paragraph.length) blocks.push({ kind: 'paragraph', spans: parseSpans(paragraph.join(' ')) });
      if (bullets.length) blocks.push({ kind: 'bullet', items: bullets });
      paragraph = [];
      bullets = [];
      // `?? ''` because the index signature is checked: both groups always match when the
      // regex did, and the compiler cannot know that.
      const hashes = heading[1] ?? '';
      blocks.push({ kind: 'heading', level: hashes.length, spans: parseSpans(heading[2] ?? '') });
      continue;
    }

    if (bullet) {
      if (paragraph.length) blocks.push({ kind: 'paragraph', spans: parseSpans(paragraph.join(' ')) });
      paragraph = [];
      bullets.push(parseSpans(bullet[1] ?? ''));
      continue;
    }

    if (!line.trim()) {
      if (paragraph.length) blocks.push({ kind: 'paragraph', spans: parseSpans(paragraph.join(' ')) });
      if (bullets.length) blocks.push({ kind: 'bullet', items: bullets });
      paragraph = [];
      bullets = [];
      continue;
    }

    if (bullets.length) {
      blocks.push({ kind: 'bullet', items: bullets });
      bullets = [];
    }
    paragraph.push(line);
  }

  if (paragraph.length) blocks.push({ kind: 'paragraph', spans: parseSpans(paragraph.join(' ')) });
  if (bullets.length) blocks.push({ kind: 'bullet', items: bullets });
  return blocks;
}
